import React, { useEffect } from "react";
import { wire } from "react-hot-wire";
import { WithTranslation, withTranslation } from "react-i18next";

import { Job, JobState, JobTrigger } from "models/job/Job";
import { Table } from "components/common/table/Table";
import { DrawerService } from "services/drawer/Drawer.service";
import { JobStateChip } from "components/job/common/JobStateChip";
import { Application } from "models/application/Application";
import { JobService } from "services/job/Job.service";

export type ApplicationJobsProps = {
  application: Application;
  setApplicationState: Function;
  jobService: JobService;
  drawerService: DrawerService;
} & WithTranslation;

export const ApplicationJobs = ({
  application,
  setApplicationState,
  jobService,
  drawerService,
  t
}: ApplicationJobsProps) => {
  const i18nKey = "job.list";

  useEffect(() => {
    setApplicationState("");
  }, [setApplicationState]);

  return (
    <div className="modal-body overflow-auto" style={{ height: 0 }}>
      <div className="m-3">
        <Table
          title={t(`${i18nKey}.title`)}
          dataFetcher={query =>
            jobService.list({
              query: {
                ...query,
                filters: {
                  ...query.filters,
                  application: application.id
                }
              }
            })
          }
          options={{
            search: false,
            pagination: true,
            disableCheckbox: true,
            refresh: 5000
          }}
          filters={[
            {
              type: "date-range",
              name: "createdAt",
              label: t(`${i18nKey}.filter.createdAt.label`)
            },
            {
              type: "select",
              name: "type",
              label: t(`${i18nKey}.filter.type.label`),
              options: [
                {
                  value: "",
                  label: t(`${i18nKey}.filter.type.option.all`)
                },
                {
                  value: "main",
                  label: t(`${i18nKey}.filter.type.option.main`),
                  default: true
                },
                {
                  value: "children",
                  label: t(`${i18nKey}.filter.type.option.children`)
                }
              ]
            },
            {
              type: "select",
              name: "state",
              label: t(`${i18nKey}.filter.state.label`),
              options: [
                {
                  value: "",
                  label: t(`${i18nKey}.filter.state.all`)
                },
                ...Object.values(JobState)
                  .filter(jobState => jobState !== JobState.unknown)
                  .map(jobState => ({
                    value: jobState,
                    label: t(`job.state.${jobState}`)
                  }))
              ]
            },
            {
              type: "select",
              name: "trigger",
              label: t(`${i18nKey}.filter.trigger.label`),
              options: [
                {
                  value: "",
                  label: t(`${i18nKey}.filter.state.all`)
                },
                ...Object.values(JobTrigger).map(jobTrigger => ({
                  value: jobTrigger,
                  label: t(`job.trigger.${jobTrigger}`)
                }))
              ]
            }
          ]}
          columns={[
            {
              field: "id",
              title: t(`${i18nKey}.column.id`)
            },
            {
              field: "name",
              title: t(`${i18nKey}.column.name`),
              sorting: false
            },
            {
              field: "createdAt",
              title: t(`${i18nKey}.column.createdAt`),
              render: job => job.createdAt.format("DD.MM.YYYY H:mm:ss")
            },
            {
              field: "startedAt",
              title: t(`${i18nKey}.column.startedAt`),
              render: job => (
                <>
                  {job.startedAt ? (
                    <span
                      title={`${job.startedAt.format("DD.MM.YYYY H:mm:ss")}`}
                    >
                      {job.startedAt.format("DD.MM.YYYY")}
                    </span>
                  ) : (
                    "---"
                  )}
                </>
              )
            },
            {
              field: "finishedAt",
              title: t(`${i18nKey}.column.finishedAt`),
              render: job => (
                <>
                  {job.finishedAt ? (
                    <span
                      title={`${job.finishedAt.format("DD.MM.YYYY H:mm:ss")}`}
                    >
                      {job.finishedAt.format("DD.MM.YYYY")}
                    </span>
                  ) : (
                    "---"
                  )}
                </>
              )
            },
            {
              field: "handler",
              title: t(`${i18nKey}.column.handler`),
              sorting: false
            },
            {
              field: "trigger",
              title: t(`${i18nKey}.column.trigger`),
              sorting: false
            },
            {
              field: "state",
              title: t(`${i18nKey}.column.state`),
              sorting: false,
              render: job => (
                <JobStateChip
                  job={job as Job}
                  onClick={() =>
                    drawerService.activate("preview-job", {
                      id: `preview-job-children-${job.id}`,
                      job
                    })
                  }
                />
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default wire(
  ["jobService", "drawerService"],
  withTranslation()(ApplicationJobs)
);
